import {
 useContext, useState
} from 'react';
import {
 Label
} from 'shared/components/ui/label';
import {
 Switch
} from 'shared/components/ui/switch';
import {
 Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 V2OpCodeBuilderContext
} from '../V2OpCodeBuilderContext';
import OELCodeEditor, {
 OELContext
} from '../components/OELCodeEditor';
import StepControllers from '../components/StepControllers';
import {
 LLMStep
} from 'builders/builderOpcode/jsonMode/types';
import {
 Dialog, DialogContent, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
 ScrollArea
} from 'shared/components/ui/scroll-area';
import {
 Card, CardContent
} from 'shared/components/ui/card';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Input
} from 'shared/components/ui/input';

const V2StepLLM = ({
    step,
    onStepChange,
    oelContext,
}: {
    step: LLMStep;
    onStepChange: (step: LLMStep) => void;
    oelContext: OELContext;
}) => {
    const {
 instructions, IsInstructionLoading
} = useContext(V2OpCodeBuilderContext);
    const [instructionModalOpen, setInstructionModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Strip OEL single quotes for display
    const bareUniqueName = step.unique_name || '';
    const bareModelType = step.model_type || '';

    const handleInstructionSelect = (unique_name: string, model_type: string) => {
        onStepChange({
            ...step,
            unique_name,
            model_type,
        });
        setInstructionModalOpen(false);
    };

    const filteredInstructions = instructions.filter((inst) =>
        inst.unique_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inst.model_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-4">
            {/* Instruction Selector */}
            <div className="space-y-2">
                <Label>Instruction <span className="text-destructive">*</span></Label>
                {bareUniqueName ? (
                    <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="font-mono text-sm py-1 px-3">
                            {bareUniqueName}
                        </Badge>
                        <span className="text-xs text-muted-foreground">({bareModelType})</span>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setInstructionModalOpen(true)}
                        >
                            Change
                        </Button>
                    </div>
                ) : (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setInstructionModalOpen(true)}
                        className="w-full"
                        disabled={IsInstructionLoading}
                    >
                        {IsInstructionLoading ? 'Loading instructions...' : 'Select Instruction'}
                    </Button>
                )}
            </div>

            {/* Query */}
            <OELCodeEditor
                label="Query"
                value={step.inputs?.query || ''}
                onChange={(val) => onStepChange({
                    ...step,
                    inputs: {
 ...step.inputs,
query: val
}
                })}
                placeholder="Type @ to reference inputs or previous steps"
                multiline
                required
                oelContext={oelContext}
                helpText="Use @input.variable_name or @step_id.output to reference data"
            />

            {/* Image URLs */}
            <OELCodeEditor
                label="Image URLs"
                value={step.inputs?.image_urls || ''}
                onChange={(val) => onStepChange({
                    ...step,
                    inputs: {
 ...step.inputs,
query: step.inputs?.query || '',
image_urls: val
}
                })}
                placeholder="Optional: OEL expression for image URLs"
                oelContext={oelContext}
            />

            {/* Output Type */}
            <div className="space-y-1">
                <Label>Output Type</Label>
                <Select
                    value={step.output_type || 'string'}
                    onValueChange={(val) => onStepChange({
                        ...step,
                        output_type: val as LLMStep['output_type']
                    })}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="string">String</SelectItem>
                        <SelectItem value="dict">Dictionary</SelectItem>
                        <SelectItem value="list">List</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Stream */}
            <div className="flex items-center gap-3">
                <Switch
                    checked={step.stream || false}
                    onCheckedChange={(checked) => onStepChange({
                        ...step,
                        stream: checked,
                        ...(checked ? {
 output_type: 'string'
} : {})
                    })}
                />
                <Label>Stream Output</Label>
                {step.stream && (
                    <span className="text-xs text-muted-foreground">(forces string output type)</span>
                )}
            </div>

            {/* Controllers */}
            <StepControllers
                when={step.when}
                forCtrl={step.for}
                repeatUntil={step.repeat_until}
                onWhenChange={(val) => onStepChange({
 ...step,
when: val
})}
                onForChange={(val) => onStepChange({
 ...step,
for: val
})}
                onRepeatUntilChange={(val) => onStepChange({
 ...step,
repeat_until: val
})}
                oelContext={oelContext}
            />

            {/* Instruction Selection Modal */}
            <Dialog open={instructionModalOpen} onOpenChange={setInstructionModalOpen}>
                <DialogContent className="max-w-[800px]">
                    <DialogHeader>
                        <DialogTitle>Select Instruction</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search instructions..."
                        />
                        <ScrollArea className="max-h-[400px]">
                            <div className="space-y-2">
                                {filteredInstructions.map((inst) => (
                                    <Card
                                        key={inst.unique_name}
                                        className={`cursor-pointer hover:bg-accent transition-colors ${
                                            bareUniqueName === inst.unique_name ? 'border-primary' : ''
                                        }`}
                                        onClick={() => handleInstructionSelect(inst.unique_name, inst.model_type)}
                                    >
                                        <CardContent className="p-3">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <p className="font-mono font-semibold text-sm">{inst.unique_name}</p>
                                                    <p className="text-xs text-muted-foreground">{inst.model_type} - {inst.modelId}</p>
                                                </div>
                                                {!inst.isModelProviderExist && (
                                                    <Badge variant="destructive" className="text-xs">Provider unavailable</Badge>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                                {filteredInstructions.length === 0 && (
                                    <p className="text-center text-sm text-muted-foreground py-4">
                                        No instructions found.
                                    </p>
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default V2StepLLM;
